import React, { useEffect, useRef, useState } from 'react';
import {
  sabitBirimleri, sabitOzet, kovaAdi, sabitTasi, sabitCikar, sabitDegistir, sabitEkle,
  numaraListesiAyristir, numaraListesiPlanla,
} from './yardimcilar';
import { satildiMi } from '../../lib/satis';
import { sikHarfleri } from '../../lib/dersler.js';

// SABİT LİSTE DÜZENLEYİCİ (Basım Faz 2, 2026-10-09, kullanıcı isteği): deneme listesindeki her ders
// bloğu tablodur — satırda numara, ÖÇ, puan/kova, harf, çift; ↑ ↓ (çift tek birim) · ⇄ Değiştir
// (aynı ÖÇ + aynı zorluktan adaylar, POST /basim/aday) · ✕ Çıkar · + numara ekle. Üstte ders
// göstergesi: ÖÇ çeşitliliği, zorluk kovası, harf payı (tavan %30). A/B kapalıyken sıra kitapçıkta aynen.

const _token = () => localStorage.getItem('yks_token');
const _basliklar = () => ({ 'Content-Type': 'application/json', ...(_token() ? { Authorization: `Bearer ${_token()}` } : {}) });

// Kimlik → batch satırı önbelleği; eksik kimlikler tek istekle getirilir (en çok 500).
export function useSoruDetay(apiUrl, ids) {
  const [detay, setDetay] = useState({});
  const istenen = useRef(new Set());
  const anahtar = (ids || []).join(',');
  useEffect(() => {
    const eksik = (ids || []).filter((id) => id && !istenen.current.has(id));
    if (eksik.length === 0) return;
    eksik.forEach((id) => istenen.current.add(id));
    fetch(`${apiUrl}/api/sorular/batch`, { method: 'POST', headers: _basliklar(), body: JSON.stringify({ ids: eksik.slice(0, 500) }) })
      .then((r) => r.json())
      .then((d) => {
        if (!Array.isArray(d.sorular)) return;
        setDetay((o) => { const n = { ...o }; d.sorular.forEach((s) => { n[s.id] = s; }); return n; });
      })
      .catch(() => { eksik.forEach((id) => istenen.current.delete(id)); });
  }, [apiUrl, anahtar]);
  return detay;
}

const KOVA_SINIF = { kolay: 'bg-emerald-100 text-emerald-800', orta: 'bg-amber-100 text-amber-800', zor: 'bg-indigo-100 text-indigo-800' };

function satirUyarisi(s, yayineviId) {
  if (!s) return null;
  if (s.durum !== 'onaylandi') return 'onaylı değil';
  if (Number(s.zorluk_puan) === 1 || Number(s.ogretmen_puani) === 1) return '1 puanlı';
  if (satildiMi(s.satis) && !(yayineviId != null && s.satis && Number(s.satis.yayinevi_id) === Number(yayineviId))) return 'satılmış';
  if (Array.isArray(s.grup_eksik_uyeler) && s.grup_eksik_uyeler.length > 0) {
    return `kardeşi eksik: ${s.grup_eksik_uyeler.map((u) => `#${u.public_no}`).join(', ')}`;
  }
  return null;
}

export default function SabitListe({ apiUrl, d, detay, tumNolar, yayineviId, puanMin, puanMax, onDegis }) {
  const [aday, setAday] = useState(null);   // { birim, yukleniyor, liste, hata, tohum }
  const [ekGirdi, setEkGirdi] = useState('');
  const [ekHata, setEkHata] = useState('');
  const birimler = sabitBirimleri(d.sabitIds, detay);
  const ozet = sabitOzet(d.sabitIds, detay);
  const hazir = ozet.yuklu === ozet.n;   // ayrıntı gelmeden işlem yok (çift tek birim tanınmaz)
  const noOf = (id, i) => (detay[id] && detay[id].public_no != null ? Number(detay[id].public_no)
    : (Array.isArray(d.sabitNolar) ? d.sabitNolar[i] : null));

  const adayGetir = async (birimNo, tohum = 0) => {
    const ilk = birimler[birimNo][0];
    setAday({ birim: birimNo, yukleniyor: true, liste: [], hata: '', tohum });
    try {
      const govde = { publicNo: Number(detay[ilk].public_no), haric: tumNolar, limit: 6, tohum };
      if (yayineviId != null) govde.yayineviId = yayineviId;
      if (puanMin !== '' && puanMin != null) govde.puanMin = Number(puanMin);
      if (puanMax !== '' && puanMax != null) govde.puanMax = Number(puanMax);
      const r = await fetch(`${apiUrl}/api/boru-hatti/basim/aday`, { method: 'POST', headers: _basliklar(), body: JSON.stringify(govde) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.basarili) throw new Error(j.hata || `HTTP ${r.status}`);
      setAday({ birim: birimNo, yukleniyor: false, liste: j.adaylar || [], hata: '', tohum });
    } catch (e) {
      setAday({ birim: birimNo, yukleniyor: false, liste: [], hata: e.message || 'Adaylar alınamadı', tohum });
    }
  };

  const ekle = async () => {
    setEkHata('');
    const { nolar } = numaraListesiAyristir(ekGirdi, 50);
    const yeni = nolar.filter((n) => !tumNolar.includes(n));
    if (!yeni.length) { setEkHata(nolar.length ? 'Bu numaralar zaten listede.' : 'Numara girin.'); return; }
    try {
      const getir = async (liste) => {
        const r = await fetch(`${apiUrl}/api/sorular/batch`, { method: 'POST', headers: _basliklar(), body: JSON.stringify({ public_nolar: liste }) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !Array.isArray(j.sorular)) throw new Error(j.hata || `HTTP ${r.status}`);
        return j;
      };
      let j = await getir(yeni);
      // Bağlam çiftinin listede olmayan kardeşi birlikte eklenir (tek birim).
      const kardes = [...new Set(j.sorular.flatMap((s) => (s.grup_eksik_uyeler || []).map((u) => Number(u.public_no))))]
        .filter((n) => !yeni.includes(n) && !tumNolar.includes(n));
      if (kardes.length) j = await getir([...yeni, ...kardes]);
      const plan = numaraListesiPlanla(j.sorular, { bulunamayan: j.bulunamayan, yayineviId });
      const aile = (x) => String(x).replace(/^(tyt|ayt)_/, '');
      const yabanci = plan.dersler.filter((p) => aile(p.ders) !== aile(d.ders));
      const sorunlar = [
        ...plan.bulunamayan.map((n) => `#${n} bulunamadı`),
        ...plan.elenen.map((e) => `#${e.no} ${e.neden}`),
        ...yabanci.flatMap((p) => p.sabitNolar.map((n) => `#${n} bu derse ait değil`)),
      ];
      const uygun = plan.dersler.find((p) => aile(p.ders) === aile(d.ders));
      if (uygun) onDegis(sabitEkle(d, uygun.sabitIds, uygun.sabitNolar, detay));
      setEkHata(sorunlar.join(' · '));
      setEkGirdi('');
    } catch (e) {
      setEkHata(e.message || 'Eklenemedi');
    }
  };

  let sira = 0;
  return (
    <div data-sabit-liste={d.ders} className="w-full space-y-1">
      <div data-sabit-ozet={d.ders} className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-700">
        <span>{ozet.n} soru · <strong>{ozet.ocSayisi}</strong> farklı ÖÇ
          {ozet.ocTekrar.length > 0 && <span className="text-amber-700"> (tekrar: {ozet.ocTekrar.map((o) => `${o.kod}×${o.n}`).join(', ')})</span>}
        </span>
        <span>kolay {ozet.kova.kolay} · orta {ozet.kova.orta} · zor {ozet.kova.zor}</span>
        <span className={ozet.harfAsim.length ? 'text-red-700 font-semibold' : ''}>
          harf {sikHarfleri(d.ders).map((h) => `${h}${ozet.harf[h] || 0}`).join(' ')} (tavan {ozet.harfTavan})
          {ozet.harfAsim.length > 0 && ` ⚠ ${ozet.harfAsim.join(', ')} fazla`}
        </span>
        {!hazir && <span className="text-gray-400">ayrıntılar yükleniyor…</span>}
      </div>
      <table className="w-full text-[11px] bg-white rounded border border-amber-200">
        <tbody>
          {birimler.map((birim, bi) => (
            <React.Fragment key={birim.join('|')}>
              {birim.map((id, ui) => {
                sira += 1;
                const s = detay[id];
                const uyari = satirUyarisi(s, yayineviId);
                const kova = s ? kovaAdi(s.zorluk_puan) : null;
                return (
                  <tr key={id} data-sabit-satir={noOf(id, sira - 1)} className="border-t border-amber-100">
                    <td className="px-1 text-gray-400 w-6 text-right">{sira}</td>
                    <td className="px-1 font-mono">#{noOf(id, sira - 1)}</td>
                    <td className="px-1 font-mono text-gray-700">{s ? s.kazanim_kodu : '…'}</td>
                    <td className="px-1">{s && <span className={`px-1 rounded ${KOVA_SINIF[kova] || 'bg-gray-100'}`}>{s.zorluk_puan ?? '—'}</span>}</td>
                    <td className="px-1 font-semibold">{s ? String(s.dogru_cevap || '').trim().toUpperCase() : ''}</td>
                    <td className="px-1">{birim.length > 1 && <span className="px-1 rounded bg-sky-100 text-sky-800" title="Bağlam çifti — tek birim">çift {ui + 1}/{birim.length}</span>}</td>
                    <td className="px-1 text-red-700">{uyari}</td>
                    <td className="px-1 whitespace-nowrap text-right">
                      {ui === 0 && (
                        <>
                          <button type="button" disabled={!hazir || bi === 0} onClick={() => onDegis(sabitTasi(d, bi, -1, detay))} title="Yukarı" className="px-1 disabled:opacity-30">↑</button>
                          <button type="button" disabled={!hazir || bi === birimler.length - 1} onClick={() => onDegis(sabitTasi(d, bi, 1, detay))} title="Aşağı" className="px-1 disabled:opacity-30">↓</button>
                          <button type="button" data-degistir={noOf(id, sira - 1)} disabled={!hazir} onClick={() => adayGetir(bi)}
                            title="Aynı öğrenme çıktısı ve zorluktan başka soru öner" className="px-1 text-sky-700 disabled:opacity-30">⇄ Değiştir</button>
                          <button type="button" data-cikar={noOf(id, sira - 1)} disabled={!hazir}
                            onClick={() => { setAday(null); onDegis(sabitCikar(d, bi, detay)); }} title="Listeden çıkar" className="px-1 text-red-600 disabled:opacity-30">✕</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {aday && aday.birim === bi && (
                <tr data-aday-panel>
                  <td colSpan={8} className="px-2 py-1 bg-sky-50">
                    {aday.yukleniyor ? <span className="text-gray-500">Adaylar aranıyor…</span> : (
                      <div className="space-y-1">
                        {aday.hata && <div className="text-red-700">{aday.hata}</div>}
                        {!aday.hata && aday.liste.length === 0 && <div className="text-gray-600">Aynı öğrenme çıktısında uygun aday yok.</div>}
                        {aday.liste.map((a) => (
                          <div key={a.uyeler.map((u) => u.public_no).join('+')} data-aday={a.uyeler.map((u) => u.public_no).join('+')} className="flex flex-wrap items-center gap-2">
                            <span className="font-mono">{a.uyeler.map((u) => `#${u.public_no}`).join(' + ')}</span>
                            <span className="font-mono text-gray-600">{a.uyeler[0].kazanim_kodu}</span>
                            {a.yakin_oc && <span className="px-1 rounded bg-amber-100 text-amber-800" title="Aynı temadan, farklı öğrenme çıktısı">yakın ÖÇ</span>}
                            <span>puan {a.uyeler.map((u) => u.zorluk_puan).join('/')}</span>
                            <span className="font-semibold">{a.uyeler.map((u) => u.harf).join('/')}</span>
                            <button type="button" data-aday-sec onClick={() => { onDegis(sabitDegistir(d, bi, a.uyeler, detay)); setAday(null); }}
                              className="px-2 py-0.5 rounded bg-sky-600 text-white font-semibold">Seç</button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <button type="button" data-aday-baska onClick={() => adayGetir(bi, aday.tohum + 1)} className="px-2 py-0.5 rounded bg-white border border-sky-300">Başka öner</button>
                          <button type="button" onClick={() => setAday(null)} className="px-2 py-0.5 rounded bg-white border border-gray-300">Kapat</button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-1">
        <input data-sabit-ekle-girdi={d.ders} value={ekGirdi} onChange={(e) => setEkGirdi(e.target.value)} placeholder="+ numara ekle (#88, 90)"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ekle(); } }}
          className="px-1.5 py-0.5 rounded border border-amber-300 text-[11px] font-mono w-44" />
        <button type="button" data-sabit-ekle={d.ders} onClick={ekle} className="px-2 py-0.5 rounded bg-white border border-amber-300 text-[11px]">Ekle</button>
        {ekHata && <span className="text-[10px] text-red-700">{ekHata}</span>}
      </div>
    </div>
  );
}
